import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useInView } from '../../hooks/useInView';
import { heroData } from '../../data/mock';

const syntaxColors = {
  comment: 'text-stone-500',
  keyword: 'text-amber-400',
  string: 'text-emerald-400',
  return: 'text-rose-400',
  type: 'text-sky-400',
  success: 'text-emerald-400',
  default: 'text-stone-300',
};

export const HeroSection = () => {
  const [ref, isInView] = useInView();

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center pt-20 lg:pt-0 overflow-hidden"
    >
      {/* Subtle bg patterns */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(245,158,11,0.05),transparent_50%)]" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle, #d4d4d4 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <div
            className={`space-y-8 transition-[opacity,transform] duration-700 ease-out ${
              isInView
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8'
            }`}
          >
            <Badge
              variant="secondary"
              className="bg-amber-50 text-amber-700 border border-amber-200/60 px-3.5 py-1.5 text-xs font-semibold tracking-widest uppercase"
            >
              {heroData.badge}
            </Badge>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-extrabold text-stone-900 leading-[1.08] tracking-tight">
              {heroData.headline}
              <br />
              <span className="text-amber-600">{heroData.headlineAccent}</span>
            </h1>

            <p className="text-lg lg:text-xl text-stone-500 leading-relaxed max-w-xl">
              {heroData.subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Button
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-white px-8 h-12 text-base font-semibold shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 transition-[background-color,box-shadow] duration-300"
                onClick={() => scrollToSection(heroData.primaryCTA.href)}
              >
                {heroData.primaryCTA.label}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-stone-300 text-stone-700 hover:bg-stone-50 px-8 h-12 text-base font-medium transition-[background-color,border-color] duration-200"
                onClick={() => scrollToSection(heroData.secondaryCTA.href)}
              >
                {heroData.secondaryCTA.label}
              </Button>
            </div>
          </div>

          {/* Right — Code editor visual */}
          <div
            className={`hidden lg:block transition-[opacity,transform] duration-700 ease-out delay-200 ${
              isInView
                ? 'opacity-100 translate-x-0'
                : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-amber-500/[0.04] rounded-3xl blur-2xl" />
              <div className="relative bg-stone-950 rounded-xl border border-stone-800/80 overflow-hidden shadow-2xl">
                {/* Title bar */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-800/80 bg-stone-900/60">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-stone-700" />
                    <div className="w-3 h-3 rounded-full bg-stone-700" />
                    <div className="w-3 h-3 rounded-full bg-stone-700" />
                  </div>
                  <span className="text-xs text-stone-500 font-mono ml-2">
                    review.py
                  </span>
                </div>

                {/* Code content */}
                <div className="p-5 overflow-x-auto">
                  <pre className="text-[13px] font-mono leading-6">
                    {heroData.codeLines.map((line, i) => (
                      <div key={i} className="flex">
                        <span className="text-stone-600 w-7 text-right mr-4 select-none text-xs leading-6">
                          {i + 1}
                        </span>
                        <span className={syntaxColors[line.type] || syntaxColors.default}>
                          {line.text}
                        </span>
                      </div>
                    ))}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
